import { ButtonType, EpicIcons } from "@constellation-academy/epic-ui";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useLazyLoadQuery, useMutation, usePaginationFragment } from "react-relay";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, ButtonVariant } from "@components/button";
import { DataTable } from "@components/data-table";
import { DateTimeDisplay } from "@components/datetime-display/datetime-display.component";
import { IdDisplayField } from "@components/id-display-field";
import { useHasPermissions } from "@hooks/use-has-permissions";
import { useTranslation } from "@i18n/hooks";
import { type usersTable_AnonymizeUserMutation } from "@relay/usersTable_AnonymizeUserMutation.graphql";
import { type usersTable_Query } from "@relay/usersTable_Query.graphql";
import { type UsersTable_Refetch } from "@relay/UsersTable_Refetch.graphql";
import { type usersTable_UsersListFragment$key } from "@relay/usersTable_UsersListFragment.graphql";
import { Paths } from "@routes/paths";
import { ANONYMIZE_USER_MUTATION, QUERY, USERS_LIST_FRAGMENT } from "./users-table.graphql";
import {
	actionButtonsWrapperClass,
	emptyMessageWrapperClass,
	loadMoreButtonWrapperClass,
} from "./users-table.styles";
import { ImpersonateUserButton } from "../impersonate-user-button";
import { UsersTableFilter } from "../users-table-filter";

export const UsersTable = () => {
	const { t } = useTranslation("common");
	const data = useLazyLoadQuery<usersTable_Query>(
		QUERY,
		{ first: 20 },
		{ fetchPolicy: "network-only" },
	);

	const {
		hasNext,
		loadNext,
		data: {
			Admin: {
				Auth: { Users },
			},
		},
		refetch,
	} = usePaginationFragment<UsersTable_Refetch, usersTable_UsersListFragment$key>(
		USERS_LIST_FRAGMENT,
		data,
	);

	const [anonymizeUser] = useMutation<usersTable_AnonymizeUserMutation>(ANONYMIZE_USER_MUTATION);

	const navigate = useNavigate();

	const users = Users?.edges;

	const createEditUserOnClickHandler = (id: string) => () => {
		navigate(Paths.users.withId(id).edit.path);
	};

	const createDeleteUserOnClickHandler = (userId: string) => () => {
		confirmDialog({
			message: t("user.confirmRemove"),
			header: t("user.removeUserTitle"),
			icon: "pi pi-info-circle",
			acceptClassName: "p-button-danger",
			acceptLabel: t("actions.yes"),
			rejectLabel: t("actions.no"),
			accept: () =>
				anonymizeUser({
					variables: {
						input: {
							userId,
						},
						connections: [Users.__id],
					},
					onError: () => {
						toast.error(
							"Der Benutzer konnte gerade nicht gelöscht werden. Bitte versuche es später erneut.",
						);
					},
				}),
			reject: () => {},
		});
	};

	const handleLoadNextOnClick = () => {
		loadNext(20);
	};

	const handleFilterOnChange = (nameOrEmail?: string) => {
		refetch({
			nameOrEmail,
		});
	};

	const canModify = useHasPermissions(["UserInAccountPermission_AuthAdmin_Modify"]);

	return (
		<div className="mb-5">
			<DataTable
				header={<UsersTableFilter onChange={handleFilterOnChange} />}
				emptyMessage={
					<div className={emptyMessageWrapperClass}>
						<div className="mr-2">{t("user.noUsersFound")}</div>
					</div>
				}
				className="mb-3"
				value={users?.map((edge) => edge!.node!)}
			>
				{(Column) => (
					<>
						<Column
							header={t("table.id")}
							style={{ width: "5%" }}
							body={(user) => <IdDisplayField id={user.id} />}
						/>
						<Column header={t("table.name")} field="name" />
						<Column header={t("table.email")} field="email" />
						<Column
							header={t("table.lastSignInAt")}
							body={(user) =>
								user?.extension?.lastSignInAt ? (
									<DateTimeDisplay
										value={user.extension.lastSignInAt}
										hideTimezone
									/>
								) : (
									"-"
								)
							}
						/>
						<Column
							header={t("table.activated")}
							body={(row) => {
								return row?.activated
									? t("user.activated")
									: t("user.notActivated");
							}}
						/>
						{canModify && (
							<Column
								header={t("table.actions")}
								style={{ width: "10%" }}
								body={(user) => {
									return (
										<div className={actionButtonsWrapperClass}>
											<Button
												type={ButtonType.Button}
												onClick={createEditUserOnClickHandler(user.id)}
												tooltip={t("user.editTooltip")}
												icon={EpicIcons.PENCIL}
											/>
											<ImpersonateUserButton userId={user.id} />
											<Button
												type={ButtonType.Button}
												variant={ButtonVariant.Error}
												onClick={createDeleteUserOnClickHandler(user.id)}
												tooltip={t("user.deleteTooltip")}
												icon={EpicIcons.TRASH}
											/>
										</div>
									);
								}}
							/>
						)}
					</>
				)}
			</DataTable>
			{hasNext && (
				<div className={loadMoreButtonWrapperClass}>
					<Button
						type={ButtonType.Button}
						disabled={!hasNext}
						onClick={handleLoadNextOnClick}
						label={t("user.showMore")}
					/>
				</div>
			)}

			<ConfirmDialog />
		</div>
	);
};
